import React, { useId } from "react";
import * as RAlertDialog from "@radix-ui/react-alert-dialog";
import * as RDialog from "@radix-ui/react-dialog";
import { twMerge } from "tailwind-merge";
import { Badge, type IBadgeProps } from "@components/Badge/Badge";
import { CrossIcon } from "@components/icons";
import { type useModal } from "@hooks/useModal";
import { ModalPrimitive } from "./ModalPrimitive";

export interface IModalProps {
  modal: ReturnType<typeof useModal>;
  children?: React.ReactNode;
  badgeProps?: IBadgeProps;
  title?: string | React.ReactNode;
  subtitle?: React.ReactNode;
  className?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  containerClassName?: string;
  actionButtons?: React.ReactNode;
  displayDrawerHandle?: boolean;
}

export const Modal: React.FC<IModalProps> = (props) => {
  const {
    modal,
    children,
    title,
    subtitle,
    className,
    titleClassName,
    subtitleClassName,
    containerClassName,
    actionButtons,
    badgeProps,
  } = props;

  const { close } = modal;

  const DialogPrimitive = modal.important ? RAlertDialog : RDialog;

  const titleId = useId();
  const descriptionId = useId();

  return (
    <ModalPrimitive
      modal={modal}
      className={className}
      titleId={titleId}
      descId={descriptionId}
    >
      <div className="px-4 py-6 md:p-6">
        {badgeProps && (
          <Badge
            className="mb-4"
            variant="rounded"
            icon={badgeProps.icon}
            size="medium"
            color={badgeProps.color ?? "green"}
          />
        )}
        <DialogPrimitive.Title
          id={titleId}
          className={twMerge(
            "text-gray-900 text-lg font-medium leading-6",
            titleClassName,
          )}
        >
          {title}
        </DialogPrimitive.Title>
        {subtitle && (
          <h6
            id={descriptionId}
            className={twMerge(
              "pt-1 font-normal text-grey-dark",
              subtitleClassName,
            )}
          >
            {subtitle}
          </h6>
        )}
        {!modal.hideCloseButton && (
          <button
            className={twMerge("absolute right-4 top-4 m-[0.62rem] h-auto")}
            onClick={(e) => {
              if (modal.onCustomClose) {
                e.preventDefault();
                modal.onCustomClose?.();
                return;
              }
              close();
            }}
          >
            <CrossIcon />
          </button>
        )}
        {children && (
          <div className={twMerge("pt-5", containerClassName)}>{children}</div>
        )}
      </div>
      {actionButtons && (
        <div className="flex flex-1 items-end">
          <div className="flex w-full flex-row gap-3 border-t border-grey-light px-4 py-6 md:p-6">
            {actionButtons}
          </div>
        </div>
      )}
    </ModalPrimitive>
  );
};
